'use client';

import { useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { templateName, type StrategyCopy } from '@/lib/strategy';

/** 组合归因任务选择器：无 taskId 时的直达入口（共享回测成功任务列表） */
export default function PortfolioSelector({ zh, t }: { zh: boolean; t: StrategyCopy }) {
  const router = useRouter();
  const [chosen, setChosen] = useState('');

  const { data: tasks } = useQuery({
    queryKey: ['strategy-backtest-shared-list', 40],
    queryFn: () => api.get<any[]>('/strategy/backtest/shared?limit=40'),
  });

  const doneTasks = useMemo(
    () => (tasks ?? []).filter((x) => x.state === 'succeeded' || x.state === 'SUCCEEDED'),
    [tasks],
  );

  return (
    <div className="mx-auto w-full max-w-[1200px] px-4 pb-24 pt-4">
      <h1 className="text-xl font-bold">{t.analysisTitle}</h1>
      <p className="mt-1 text-xs text-gf-muted">
        <span className="hidden md:inline">{t.analysisSubtitle}</span>
        <span className="md:hidden">{t.analysisMobileSub}</span>
      </p>
      <div className="card mt-6 p-5">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
          <span>{t.chooseTask}</span>
          <span className="text-[11px] font-normal text-gf-muted">{t.chooseTaskHint}</span>
        </div>
        {doneTasks.length === 0 ? (
          <div className="py-10 text-center text-sm text-gf-muted">
            <p>{t.noTasks}</p>
            <p className="mt-1 text-xs">{t.noTaskHint}</p>
          </div>
        ) : (
          <ul className="divide-y divide-gf-border/60">
            {doneTasks.map((x) => (
              <li key={x.id}>
                <button
                  type="button"
                  onClick={() => {
                    if (chosen === x.id) router.replace(`/strategy/portfolios/${x.id}/analysis`);
                    else setChosen(x.id);
                  }}
                  className="flex w-full items-center justify-between gap-3 px-1 py-3 text-left transition-colors hover:bg-gf-surface2"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">
                      {templateName(x.templateKey, zh)} <span className="text-gf-muted">·</span>{' '}
                      <span className="text-xs text-gf-muted">
                        {Array.isArray(x.symbols) ? x.symbols.join(' + ') : (x.symbol ?? '')}
                      </span>
                    </span>
                    <span className="mt-0.5 block text-[11px] text-gf-muted">
                      {new Date(x.createdAt ?? x.startMs ?? Date.now()).toLocaleString()}
                      {x.totalReturnPct != null ? ` · ${x.totalReturnPct >= 0 ? '+' : ''}${Number(x.totalReturnPct).toFixed(2)}%` : ''}
                      {x.maxDrawdownPct != null ? ` · ${zh ? '回撤' : 'DD'} ${Math.abs(Number(x.maxDrawdownPct)).toFixed(2)}%` : ''}
                    </span>
                  </span>
                  <span className="shrink-0 text-xs font-medium text-gf-accent">
                    {chosen === x.id ? (zh ? '进入归因 →' : 'Open attribution →') : zh ? '查看证据' : 'View evidence'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
